import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { DEFAULT_BASIS, type Basis, type GridRow } from "./api";
import { BASIS_LABEL, countryName, money, pct1 } from "./format";
import { QUANTITIES, SCENARIO_COPY as T, scenarioBody, scenarioMissing, scenarioQuery, scenarioRefusal, shippingText, type Basket } from "./scenarios";

/**
 * The basket scenarios of a Shopify row in the New price box (C1 v1.40, c1-scenarios; Prototype v26 basketBlockHtml): one order of
 * the picked quantity to the picked ship country at the typed price, the scan to the first basket that ships free plus one (at
 * most 24, DEC-3 ruling C1) with its worst basket marked, and every gap named. C2 prices it (`POST /api/scenarios`) after the
 * typed price has rested, so a price edit re-finds the worst basket without a reload (gate H10); the last answer stays on screen,
 * marked as updating, until the next arrives, and the pickers show what was picked. Nothing here computes a figure.
 * `price` is the box's debounced, cleaned price (null asks nothing); `basis` the grid's COGS basis on view. A ship-country row
 * opens on its own country; a store row on the one C2 picks (its worst basket's, as the Price Run link names it).
 */
export function BasketScenarios({ row, price, basis = DEFAULT_BASIS }: { row: GridRow; price: string | null; basis?: Basis }) {
  const [country, setCountry] = useState<string | null>(row.grain === "ship_country" ? row.location : null);
  const [quantity, setQuantity] = useState(1);
  const query = useQuery({ ...scenarioQuery(scenarioBody(row, price ?? "", basis, country, quantity)), enabled: price !== null });
  const s = query.data;
  return (
    <section aria-label={T.title} className="mt-2 rounded-md border border-ks-grey-2 p-2 text-xs text-ks-grey-1">
      <p className="text-sm font-semibold text-ks-ink">{T.title}</p>
      <p>{T.intro}</p>
      {price === null ? <p>{T.enter}</p> : query.isError ? <p role="alert" className="text-sm">{scenarioRefusal(query.error)}</p> : !s ? (
        <p role="status">{T.pending}</p>
      ) : (
        <>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Select value={country ?? s.country ?? ""} onValueChange={(v) => setCountry(v)}>
              <SelectTrigger aria-label={T.country} className="w-44" data-nonmutating><SelectValue>{(country ?? s.country) ? countryName(country ?? s.country) : T.country}</SelectValue></SelectTrigger>
              <SelectContent>{s.countries.map((c) => <SelectItem key={c.country} value={c.country}>{countryName(c.country)}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={String(quantity)} onValueChange={(v) => setQuantity(Number(v))}>
              <SelectTrigger aria-label={T.quantity} className="w-32" data-nonmutating><SelectValue>{T.units(quantity)}</SelectValue></SelectTrigger>
              <SelectContent>{QUANTITIES.map((q) => <SelectItem key={q} value={String(q)}>{T.units(q)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {query.isPlaceholderData && <p role="status">{T.updating}</p>}
          {s.stack && <Stack b={s.stack} s={s} />}
          {s.worst ? (
            <p className="mt-1 text-sm text-ks-ink">{T.worst(T.units(s.worst.quantity), pct1(s.worst.margin_pct), money(s.worst.profit_per_unit, s.currency))}</p>
          ) : s.scan.length > 0 && <p className="mt-1">{T.noWorst}</p>}
          {s.scan.length > 0 && (
            <Table>
              <TableHeader><TableRow>{T.scanHead.map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {s.scan.map((b) => {
                  const worst = s.worst?.quantity === b.quantity;
                  return (
                    <TableRow key={b.quantity} data-worst={worst ? "true" : undefined}>
                      <TableCell>{b.quantity}{worst && <> <Badge variant="outline" className="border-ks-warn text-ks-warn">{T.worstChip}</Badge></>}</TableCell>
                      <TableCell className="tabular-nums">{money(b.basket, s.currency)}</TableCell>
                      <TableCell>{b.free_shipping ? T.shipFree : b.shipping_paid === null ? T.shipUnknown : T.shipPaid}</TableCell>
                      <TableCell className="tabular-nums">{money(b.profit_per_unit, s.currency)}</TableCell>
                      <TableCell className="tabular-nums">{pct1(b.margin_pct)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          {s.cap && <p>{s.cap.capped ? T.capped : s.cap.rule}</p>}
          {s.missing.length > 0 && <p role="status">{T.missing(s.missing.map(scenarioMissing).join(", "))}</p>}
          <p>{T.footer(BASIS_LABEL[s.cogs_basis])}</p>
        </>
      )}
    </section>
  );
}

/** The basket at the picked quantity: how it ships, the revenue, the costs and the profit, as C2 states them. */
function Stack({ b, s }: { b: Basket; s: { currency: string; definition: Parameters<typeof shippingText>[1] } }) {
  const cur = s.currency;
  return (
    <div className="mt-1">
      <p className="text-ks-ink">{T.basket(money(b.basket, cur), shippingText(b, s.definition, cur))}</p>
      <p>{T.revenue(b.revenue_ex_vat === null ? null : money(b.revenue_ex_vat, cur), money(b.goods_ex_vat, cur), b.shipping_ex_vat === null ? null : money(b.shipping_ex_vat, cur))}</p>
      <p>{T.costs(money(b.cogs, cur), money(b.pick_pack, cur), money(b.packaging, cur), money(b.courier, cur), b.other == null ? null : money(b.other, cur))}</p>
      <p className="text-ks-ink">{b.profit_per_order === null ? T.noProfit : T.profit(money(b.profit_per_order, cur), money(b.profit_per_unit, cur), pct1(b.margin_pct))}</p>
    </div>
  );
}
